import type { LegalHeuristicsStats } from "../heuristics/legalParagraphs";
import type {
  NumberingFidelityStats,
  ParagraphFidelityStats,
  TableFidelityStats,
  TextFidelityStats,
} from "../types";

export type ImportQualityTier = "excellent" | "good" | "review_recommended";

export interface ImportFidelityReport {
  qualityTier: ImportQualityTier;
  estimatedPreservationPercent: number;
  issues: string[];
  unsupportedFeatures: string[];
  preserved: string[];
  reviewRecommended: string[];
  /** One-line lawyer-friendly summary */
  summary: string;
}

export interface FidelityReportInput {
  html: string;
  warnings: { code: string; message: string }[];
  mammothMessages?: { code: string; message: string }[];
  paragraphStats?: ParagraphFidelityStats;
  numberingStats?: NumberingFidelityStats;
  tableStats?: TableFidelityStats;
  textStats?: TextFidelityStats;
  legalHeuristicsStats?: LegalHeuristicsStats;
}

export function detectComplexNumbering(html: string): boolean {
  return (
    /\(\s*[a-z]\s*\)/i.test(html) ||
    /\(\s*(i|ii|iii|iv|v|vi|vii|viii|ix|x)\s*\)/i.test(html) ||
    /<ol[^>]*>/i.test(html)
  );
}

function collectPreserved(
  html: string,
  input: FidelityReportInput,
): string[] {
  const {
    paragraphStats,
    numberingStats,
    tableStats,
    textStats,
    legalHeuristicsStats,
  } = input;
  const numberingApplied = (numberingStats?.numbersApplied ?? 0) > 0;
  const preserved: string[] = [];

  if (/text-align:\s*(center|right|justify|left)/i.test(html)) {
    preserved.push("Alignment");
  }
  if (
    /text-indent:\s*-?\d+px/i.test(html) ||
    /margin-left:\s*\d+px/i.test(html) ||
    (paragraphStats?.appliedIndent ?? 0) > 0
  ) {
    preserved.push("Indents");
  }
  if (
    /margin-top:\s*\d+px/i.test(html) ||
    /margin-bottom:\s*\d+px/i.test(html) ||
    (paragraphStats?.appliedSpacing ?? 0) > 0
  ) {
    preserved.push("Paragraph spacing");
  }
  if (/line-height:\s*[\d.]+/i.test(html) || (paragraphStats?.appliedLineHeight ?? 0) > 0) {
    preserved.push("Line spacing");
  }
  if (numberingApplied || /data-lexgat-num/i.test(html) || /<ol[^>]*start=/i.test(html)) {
    preserved.push("Legal numbering");
  }
  if (
    (tableStats?.tablesEnriched ?? 0) > 0 ||
    /lexgat-import-table/i.test(html) ||
    /colspan=/i.test(html)
  ) {
    preserved.push("Tables");
  }
  if (
    (textStats?.paragraphsEnriched ?? 0) > 0 ||
    /<strong>|<em>|<u>|<sup>|<sub>/i.test(html) ||
    /font-size:\s*\d/i.test(html)
  ) {
    preserved.push("Inline formatting");
  }
  if ((legalHeuristicsStats?.totalApplied ?? 0) > 0) {
    preserved.push("Legal layout patterns");
  }

  return [...new Set(preserved)];
}

const ALWAYS_UNSUPPORTED = [
  "Headers and footers",
  "Footnotes and endnotes",
  "Text boxes and floating objects",
  "Comments and track changes",
];

function collectReviewRecommended(
  html: string,
  input: FidelityReportInput,
): string[] {
  const { warnings, numberingStats } = input;
  const numberingApplied = (numberingStats?.numbersApplied ?? 0) > 0;
  const items = [...ALWAYS_UNSUPPORTED];

  if (warnings.some((w) => w.code === "complex_numbering")) {
    items.push("Complex legal numbering (partial import)");
  } else if (!numberingApplied && detectComplexNumbering(html)) {
    items.push("Numbered pleading structure");
  }

  return [...new Set(items)];
}

function buildSummary(
  tier: ImportQualityTier,
  preserved: string[],
  reviewRecommended: string[],
  percent: number,
): string {
  if (tier === "excellent") {
    if (preserved.length >= 3) {
      return `Excellent import (${percent}% estimated) — ${preserved.slice(0, 4).join(", ").toLowerCase()} preserved.`;
    }
    return `Excellent import (${percent}% estimated) — formatting closely matches your Word document.`;
  }
  if (tier === "good") {
    return `Good import (${percent}% estimated) — most formatting preserved; check items marked for review.`;
  }
  return `Review recommended (${percent}% estimated) — verify formatting against your original Word file.`;
}

export function buildImportFidelityReport(
  input: FidelityReportInput,
): ImportFidelityReport {
  const { html, warnings, mammothMessages = [] } = input;
  const issues: string[] = [];
  const unsupportedFeatures: string[] = ["Headers and footers"];

  if (warnings.some((w) => w.code === "complex_numbering")) {
    issues.push("Complex numbering may need manual review");
    unsupportedFeatures.push("Multilevel outline numbering (partial)");
  }

  const numberingApplied = (input.numberingStats?.numbersApplied ?? 0) > 0;
  if (!numberingApplied && detectComplexNumbering(html)) {
    issues.push("Numbered pleading structure may need review");
  }

  if (mammothMessages.length > 0) {
    issues.push("Some Word elements were simplified during conversion");
  }

  unsupportedFeatures.push(
    "Comments and track changes",
    "Footnotes and endnotes",
    "Text boxes",
  );

  const penalty =
    (warnings.some((w) => w.code === "complex_numbering") ? 15 : 0) +
    (!numberingApplied && detectComplexNumbering(html) ? 10 : 0) +
    Math.min(mammothMessages.length * 3, 20) +
    (html.trim().length < 50 ? 40 : 0);

  const estimatedPreservationPercent = Math.max(55, Math.min(98, 95 - penalty));

  let qualityTier: ImportQualityTier = "excellent";
  if (estimatedPreservationPercent < 75 || !html.trim()) {
    qualityTier = "review_recommended";
  } else if (estimatedPreservationPercent < 88 || issues.length > 1) {
    qualityTier = "good";
  }

  if (!html.trim()) {
    issues.unshift("The document appears to be empty after conversion");
  }

  const preserved = collectPreserved(html, input);
  const reviewRecommended = collectReviewRecommended(html, input);

  return {
    qualityTier,
    estimatedPreservationPercent,
    issues: [...new Set(issues)],
    unsupportedFeatures: [...new Set(unsupportedFeatures)],
    preserved,
    reviewRecommended,
    summary: buildSummary(
      qualityTier,
      preserved,
      reviewRecommended,
      estimatedPreservationPercent,
    ),
  };
}
